// @morgens-hub/contracts — ts-rest contract definitions
//
// Source-only ESM csomag: a routerek EBBEN a fájlban élnek (a relatív al-import
// futásidőben nem oldódna fel). Új feature contractja ide kerül, majd a root
// `hubContract`-ba aggregáljuk. Szabályok: packages/contracts/CLAUDE.md.

import {
  ARTICLE_STATUSES,
  isAllowedUploadType,
  isValidAppearance,
  REVISION_SOURCES,
  VISIBILITIES,
} from '@morgens-hub/shared';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ── Közös építőkövek ──

/**
 * GÉPI HIBAKÓDOK — a hibakezelés szerződése.
 *
 * Miért kell: a HTTP-státusz túl tág. Egy 503 lehet „az Odin épp nem elérhető, próbáld újra" ÉS
 * „a rendszer-fiók nincs beállítva, ezt egy kollégának kell rendezni" — a felhasználó számára ez a
 * két helyzet gyökeresen más (várjon-e, próbáljon-e újra, szóljon-e valakinek). A `message` erre nem
 * elég: emberi szöveg, amit a felület nem elemezhet, és a nyelve/megfogalmazása bármikor változhat.
 *
 * A `code` MINDIG stabil, gépi kulcs. A felület ebből választ szöveget (`errors.<code>` i18n) és
 * viselkedést (újrapróbálható-e, blokkoló-e, hova irányítson) — nem a státuszból, nem a szövegből.
 *
 * A készlet szándékosan SZŰK: minden kód egy VALÓDI, a felületen máshogy kezelendő helyzet. Ha egy új
 * kódhoz nem tartozik saját üzenet és saját cselekvés, akkor nem kell — a státusz-alapú default elég.
 */
export const HUB_ERROR_CODES = [
  // ── A hívó oldalán rendezhető ──
  /** Nincs (vagy lejárt) munkamenet → belépés. */
  'unauthenticated',
  /** A művelethez hiányzik a feature-jog. Újrapróbálás nem segít; jogot kell kérni. */
  'permission_denied',
  /** A kért erőforrás nincs (vagy törölték). */
  'not_found',
  /** Hibás kérés (rossz azonosító, hiányos lista) — kód-hiba vagy elavult felület-állapot. */
  'invalid_request',
  /** Séma-/tartalom-validáció bukott (pl. túl nagy fájl, nem engedett típus). */
  'validation_failed',
  /** Időközben más is módosította (OCC) — a mentés NEM íródott felül; újratöltés kell. */
  'conflict',
  /** Túl sok kérés/próbálkozás — várni kell. */
  'rate_limited',

  // ── A rendszer oldalán rendezhető (a felhasználó nem tud vele mit tenni) ──
  /**
   * A Hub Odin **rendszer-fiókja** (service-identity) nincs beállítva, vagy nincs írás-joga → a
   * SHARED buszra semmilyen írás nem megy: szerkesztés, publikálás, hozzászólás. NEM múló hiba és
   * NEM a felhasználó hibája: üzemeltetői beállításra vár, ezért az újrapróbálás értelmetlen.
   */
  'service_identity_missing',
  /**
   * A fájl-tár **cél-ügyfele** (`HUB_INTERNAL_CLIENT_ID`) nincs beállítva → kép/csatolmány nem
   * tölthető fel, nem véglegesíthető és nem törölhető. Az Odin fájl-metaadata tenant-scope-olt
   * (`platform.file_metadata.client_id` NOT NULL); a `knowledge` SHARED volta csak a resource-BUSZRA
   * vonatkozik, a fájl-tárra nem. KÜLÖN kód a `service_identity_missing`-től: ott a FIÓK hiányzik (az
   * írás egésze áll), itt CSAK a fájl-műveletek — a cikk-mentés és a hozzászólás közben működik. A
   * kettő összemosása épp azt a félrediagnosztizálást hozná vissza, ami ezt a hibát háromszor
   * visszaküldte. Ugyanúgy üzemeltetői beállításra vár: az újrapróbálás értelmetlen.
   */
  'file_tenant_missing',
  'internal_tenant_missing',
  /** Az Odin (upstream) nem elérhető vagy hibázott — jellemzően múló, érdemes újrapróbálni. */
  'upstream_unavailable',
  /** A keresőindex nem elérhető/üres — a böngészés működik, a keresés nem ad találatot. */
  'index_unavailable',
  /** Váratlan szerverhiba (a részletek a naplóban, a kliens felé nem szivárog). */
  'internal_error',
] as const;

export const HubErrorCodeSchema = z.enum(HUB_ERROR_CODES);
export type HubErrorCode = (typeof HUB_ERROR_CODES)[number];

/**
 * Minden hibaválasz alakja. A `code` **opcionális a sémában**, de a BFF-en a globális hiba-filter
 * MINDIG kitölti — az opcionalitás csak azt engedi meg, hogy egy régi/idegen közvetítő (proxy-hibalap)
 * válasza is illeszkedjen, ne a validáláson haljon el a hibakezelés.
 */
const ErrorSchema = z.object({
  message: z.string(),
  code: HubErrorCodeSchema.optional(),
});
export type ErrorBody = z.infer<typeof ErrorSchema>;

// ── Auth (P0 — az Odin iam BFF-proxyja) ──

export const LoginBodySchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  /** „Emlékezz rám": true → perzisztens refresh-cookie (30 nap); false → session-cookie. */
  rememberMe: z.boolean().optional(),
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

/** A bejelentkezett identity a JWKS-validált `aud: morgens-hub` tokenből (P0 walking skeleton). */
export const SessionSchema = z.object({
  sub: z.string(),
  scope: z.string(),
  /** Az app-scope-olt feature-jogok (`article.manage`, … ) — a menü-láthatósághoz. */
  permissions: z.array(z.string()),
  /** Kényelmi jelző: platform-admin (superuser) — a UI mindent mutat. */
  isPlatformAdmin: z.boolean(),
  /** OIDC profil-claimek (avatar-monogram, personalizáció). */
  email: z.string().nullable(),
  name: z.string().nullable(),
  givenName: z.string().nullable(),
  familyName: z.string().nullable(),
  /**
   * true = a Hub service-identity NÉLKÜL fut (csak-olvasó mód): minden SHARED-írás — szerkesztés,
   * komment, publikálás — 503-at ad. A felület ebből tudja ELŐRE jelezni az állapotot, ahelyett,
   * hogy a felhasználó a mentésnél futna hibába.
   */
  readOnly: z.boolean(),
});
export type Session = z.infer<typeof SessionSchema>;

// ── Knowledge — olvasó felület (P1b, F1-ben átszabva a valós méretre) ──
//
// MÉRETEZÉSI DÖNTÉS (F0-mérés a migrált tudástár nagyságrendjén: 42 terület · 430 cikk):
// a NAVIGÁCIÓS FA csak szerkezetet ad darabszámokkal (~15 KB), a cikk-listák ághoz kötöttek
// (`knowledgeSpace`) — a korábbi „minden cikk a fában" alak 137 KB-ot és 1148 DOM-linket jelentett.
// A listázás továbbra is a resource-buszról jön (BFF-cache mögött); a P1d-s derived index váltja ki.

/** Cikk-előnézet listákhoz (terület-oldal, tag-oldal, kezdőlap). */
export const ArticleStubSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  tags: z.array(z.string()),
  updatedAt: z.string(),
  /**
   * A cikk életciklus-állapota — a felület ebből jelöli meg a VÁZLATOT.
   *
   * MIÉRT KELL a listákban: a belső nézet 2026-08-14-től a vázlatokat IS mutatja (enélkül a szerző
   * nem találta vissza a saját, még nem publikált munkáját — élesben 17 ilyen cikk volt). Egy
   * jelöletlen vázlat viszont publikáltnak LÁTSZANA, ezért a státusz a szerverről jön, nem a
   * felület találgatja. A PUBLIKUS súgó válaszaiban ez definíció szerint mindig `published`: a
   * `fetchContent` közönség-szűrője csak azt engedi ki (lásd ott a biztonsági kommentet).
   */
  status: z.enum(ARTICLE_STATUSES),
  /**
   * Elérhető-e a cikk a publikus súgóban — EFFEKTÍV láthatóság (szekció ∧ könyv ∧ cikk), nem a
   * cikk saját `visibility` mezője. Lásd `ArticlePageSchema.publiclyVisible`; ugyanaz az érv, csak
   * a listákra: a felület egyetlen elem saját mezőjéből nem tudja, kikerült-e a súgóra.
   *
   * A PUBLIKUS végponton definíció szerint mindig `true`.
   */
  publiclyVisible: z.boolean(),
});
export type ArticleStub = z.infer<typeof ArticleStubSchema>;

/** Fa-csomópont: kategória cikk-darabszámmal (a cikkek a `knowledgeSpace` válaszában jönnek). */
export const CategoryNodeSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  articleCount: z.number(),
  /** Effektív láthatóság: szekció ∧ könyv — lásd `ArticleStubSchema.publiclyVisible`. */
  publiclyVisible: z.boolean(),
});
export type CategoryNode = z.infer<typeof CategoryNodeSchema>;

export const SpaceNodeSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  /** heroicons-név (a seedelt séma `icon` mezője) — a web ikonná képezi, ismeretlenre fallback. */
  icon: z.string(),
  articleCount: z.number(),
  /** A szekció saját `visibility`-je nyilvános-e — a hierarchia FELSŐ szintje. */
  publiclyVisible: z.boolean(),
  categories: z.array(CategoryNodeSchema),
});
export type SpaceNode = z.infer<typeof SpaceNodeSchema>;

/** A navigációs fa: terület → kategória (+ darabszámok). Cikkeket NEM tartalmaz. */
export const KnowledgeTreeSchema = z.object({
  spaces: z.array(SpaceNodeSchema),
});
export type KnowledgeTree = z.infer<typeof KnowledgeTreeSchema>;

/** Egy terület teljes tartalma: a kategóriái a cikkeikkel (terület-oldal + a fa aktív ága). */
export const SpacePageSchema = z.object({
  space: z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    description: z.string(),
    icon: z.string(),
    publiclyVisible: z.boolean(),
  }),
  categories: z.array(
    z.object({
      id: z.string(),
      slug: z.string(),
      name: z.string(),
      publiclyVisible: z.boolean(),
      articles: z.array(ArticleStubSchema),
    }),
  ),
});
export type SpacePage = z.infer<typeof SpacePageSchema>;

/** Kezdőlap: legutóbb frissített cikkek (a 42 terület-kártya mellé belépési pont). */
export const RecentArticleSchema = ArticleStubSchema.extend({
  spaceSlug: z.string(),
  spaceName: z.string(),
  categorySlug: z.string(),
  categoryName: z.string(),
});
export const RecentArticlesSchema = z.object({
  articles: z.array(RecentArticleSchema),
});
export type RecentArticles = z.infer<typeof RecentArticlesSchema>;

export const ArticleViewSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  /** Markdown-forrás — a web sanitizált renderrel jeleníti meg (nyers HTML nem fut le). */
  body: z.string(),
  tags: z.array(z.string()),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
  /** Lásd az `ArticleStubSchema.status` doc-ját — a cikk-oldalon is jelölni kell a vázlatot. */
  status: z.enum(ARTICLE_STATUSES),
});
export type ArticleView = z.infer<typeof ArticleViewSchema>;

/** A kanonikus útvonal-szegmensek — alias-slugon (átnevezett/áthelyezett cikk) érkező kérésnél
 * a kliens erre irányít át (SPA-megfelelője a spec szerinti 301-nek). */
export const CanonicalPathSchema = z.object({
  spaceSlug: z.string(),
  categorySlug: z.string(),
  articleSlug: z.string(),
});

export const ArticlePageSchema = z.object({
  space: z.object({ slug: z.string(), name: z.string() }),
  category: z.object({ slug: z.string(), name: z.string() }),
  article: ArticleViewSchema,
  canonical: CanonicalPathSchema,
  /**
   * Elérhető-e a cikk a publikus súgóban is, UGYANEZEN az útvonalon (`/sugo/...`).
   *
   * EFFEKTÍV láthatóság, nem a cikk saját `visibility` mezője: a publikus közönség a hierarchia
   * MINDHÁROM szintjét szűri, tehát egy „nyilvános" cikk belső szekcióban/könyvben állva sem
   * érhető el kívülről. A felület enélkül nem tudná eldönteni, mutathat-e „publikus nézet" linket —
   * a cikk-mező alapján 404-be küldené a szerkesztőt.
   *
   * A publikus végponton (`/public/...`) ez definíció szerint `true`.
   */
  publiclyVisible: z.boolean(),
});
export type ArticlePage = z.infer<typeof ArticlePageSchema>;

/** Tag-oldal: egy címke cikkei, terület szerint csoportosítva (spec 6.1). */
export const TagPageSchema = z.object({
  tag: z.string(),
  groups: z.array(
    z.object({
      space: z.object({ slug: z.string(), name: z.string() }),
      articles: z.array(
        ArticleStubSchema.extend({
          categorySlug: z.string(),
          categoryName: z.string(),
        }),
      ),
    }),
  ),
});
export type TagPage = z.infer<typeof TagPageSchema>;

/**
 * A leggyakoribb címkék — a kezdőlap „mire keresnek mások" belépői.
 * A darabszám a LÁTHATÓ cikkeké, tehát közönségfüggő (belső ≠ publikus).
 */
export const PopularTagsSchema = z.object({
  tags: z.array(z.object({ tag: z.string(), count: z.number().int() })),
});
export type PopularTags = z.infer<typeof PopularTagsSchema>;

// ── Kommentek (belső együttműködés) ──
//
// SZÁNDÉKOSAN CSAK BELSŐ: a publikus súgó nem ad és nem is fogad kommentet (terméktulajdonosi döntés,
// 2026-08-01). Ezért a végpontok az AUTH-os felületen élnek, és a `public.controller` nem
// szolgálja ki őket — a nyilvános cikk-oldalon a szekció meg sem jelenik.

export const CommentSchema = z.object({
  id: z.string(),
  version: z.number(),
  articleId: z.string(),
  /** Egy szint mélységű válasz-lánc (a séma is így engedi): a válasz szülője mindig gyökér. */
  parentId: z.string().nullable(),
  authorId: z.string(),
  /** Best-effort feloldás; üres, ha az identity-lista nem elérhető (a komment ettől olvasható). */
  authorName: z.string(),
  body: z.string(),
  /** Moderálás: a megválaszolt/elintézett szál lezárva, de nem törölve. */
  resolved: z.boolean(),
  createdAt: z.string(),
});
export type Comment = z.infer<typeof CommentSchema>;

export const CommentsSchema = z.object({ comments: z.array(CommentSchema) });
export type Comments = z.infer<typeof CommentsSchema>;

export const CommentInputSchema = z.object({
  body: z.string().min(1).max(5000),
  parentId: z.string().uuid().optional(),
});
export type CommentInput = z.infer<typeof CommentInputSchema>;

// ── Jog-katalógus címkék ──

export const PermissionCatalogEntrySchema = z.object({
  key: z.string(),
  /** Magyar, ember-olvasható név — a manifest `label.hu` mezője. */
  label: z.string(),
  /** A csoport magyar neve (Cikkek, Szerkezet, …) — a manifest `group.hu` mezője. */
  group: z.string(),
});
export type PermissionCatalogEntry = z.infer<typeof PermissionCatalogEntrySchema>;

export const PermissionCatalogSchema = z.object({
  permissions: z.array(PermissionCatalogEntrySchema),
});
export type PermissionCatalog = z.infer<typeof PermissionCatalogSchema>;

// ── Sablonok (2026-08-27) ──
//
// A sablon a szerkesztés gyorsítására szolgáló ÚJRAFELHASZNÁLHATÓ TARTALOM, két fajtában:
//  · `article` — cikk-VÁZ: teljes felépítés (címsorok, kitöltendő helyek), új cikk létrehozásakor;
//  · `snippet` — beszúrható RÉSZLET, a szerkesztő oldalsávjából a kurzorhoz.
//
// A `body` SZÁNDÉKOSAN ugyanaz a formátum, mint a cikk-törzs (markdown + HTML-szigetek): így a
// sablon ugyanazzal a szerkesztővel írható, mint egy cikk (nulla új szerkesztő-kód), és a beszúrás
// egyszerű szöveg-művelet marad.

export const TemplateKindSchema = z.enum(['article', 'snippet']);
export type TemplateKind = z.infer<typeof TemplateKindSchema>;

export const TemplateSchema = z.object({
  id: z.string(),
  version: z.number(),
  kind: TemplateKindSchema,
  name: z.string(),
  /** Mikor használd — a választó kártyáján jelenik meg. */
  description: z.string(),
  body: z.string(),
  /** Sorrend a listában; azonos érték esetén a név dönt. */
  position: z.number(),
  /** Kivezetés törlés helyett: az archivált sablon nem ajánlható fel, de a története megmarad. */
  archived: z.boolean(),
});
export type Template = z.infer<typeof TemplateSchema>;

export const TemplatesSchema = z.object({ templates: z.array(TemplateSchema) });
export type Templates = z.infer<typeof TemplatesSchema>;

export const TemplateInputSchema = z.object({
  kind: TemplateKindSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(300).default(''),
  body: z.string().max(50_000),
  position: z.number().int().min(0).max(999).default(0),
});
export type TemplateInput = z.infer<typeof TemplateInputSchema>;

/** Módosításnál minden mező elhagyható, de a `version` OCC-hez kötelező (If-Match fejlécben megy). */
export const TemplateUpdateSchema = TemplateInputSchema.partial().extend({
  archived: z.boolean().optional(),
  version: z.number(),
});
export type TemplateUpdate = z.infer<typeof TemplateUpdateSchema>;

// ── Keresés (F3) ──
//
// A végpont a cserélhető RETRIEVAL-KONTRAKT mögött ül (ADR-0001 §3): ma a Hub beágyazott indexe
// szolgálja ki, később a közös asszisztens-szolgáltatás (Muninn) — a szerződés nem változik.

export const SearchHitSchema = z.object({
  chunkId: z.string(),
  articleId: z.string(),
  articleTitle: z.string(),
  /** „Terület › Kategória › Cikk › Szakasz" — hol található a találat. */
  headingPath: z.string(),
  /** Kiemelt szövegkörnyezet; `<mark>` jelöléssel, a kliens SANITIZÁLVA jeleníti meg. */
  snippet: z.string(),
  spaceSlug: z.string(),
  spaceName: z.string(),
  categorySlug: z.string(),
  categoryName: z.string(),
  articleSlug: z.string(),
  /** Rangsorolási pontszám; csak ugyanazon lekérdezés találatai között értelmezhető. */
  score: z.number(),
});
export type SearchHit = z.infer<typeof SearchHitSchema>;

/** Hatókör: cím (cikkcím, A-súly) / tartalom (fejezet+törzs, B+C) / minden — lásd RetrievalFilter. */
export const SearchScopeSchema = z.enum(['all', 'title', 'content']);
export type SearchScope = z.infer<typeof SearchScopeSchema>;

/** Könyv/szekció-cím találat (struktúra, nem cikk) — a "Címben" hatókör az ilyen neveket is nézi. */
export const StructureHitSchema = z.object({
  id: z.string(),
  kind: z.enum(['space', 'category']),
  name: z.string(),
  spaceSlug: z.string(),
  categorySlug: z.string().nullable(),
});
export type StructureHit = z.infer<typeof StructureHitSchema>;

export const SearchResultSchema = z.object({
  query: z.string(),
  hits: z.array(SearchHitSchema),
  /** A keresés átmenetileg nem elérhető (index nincs kész / hiba) — a UI ezt jelzi. */
  degraded: z.boolean(),
  // OPCIONÁLIS: régi kliens (a mező bevezetése előtti web-build) nem törik a hiányán.
  structureHits: z.array(StructureHitSchema).optional(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

export const IndexStatusSchema = z.object({
  ready: z.boolean(),
  running: z.boolean(),
  chunks: z.number(),
  articles: z.number(),
  // I1: OPCIONÁLIS — régi kliens (a mező bevezetése előtti web-build) nem törik a hiányán. A
  // struktúra-index (könyv/szekció-cím) mérete KÜLÖN a chunk-számtól, mert a kettő egymástól
  // függetlenül üres lehet (lásd `IndexerService.ensurePopulated`) — enélkül a felület "kész"-nek
  // mutatta az indexet, miközben a "Címben" mód néma 0 találatot adott.
  structureRows: z.number().optional(),
  lastRunAt: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type IndexStatus = z.infer<typeof IndexStatusSchema>;

// A 0-találatos keresések naplója (P3) — a `knowledge_gaps` feature előfutára: MIT keresnek
// hiába a kollégák. Szándékosan NINCS felhasználó-azonosító mező (lásd a migráció kommentjét).
export const SearchMissSchema = z.object({
  q: z.string(),
  count: z.number(),
  lastAt: z.string(),
});
export type SearchMiss = z.infer<typeof SearchMissSchema>;

export const SearchMissesSchema = z.object({
  misses: z.array(SearchMissSchema),
});
export type SearchMisses = z.infer<typeof SearchMissesSchema>;

// ── Szerkesztés (F4) ──
//
// A szerkesztő NEM az olvasó-nézetet használja: ott csak a publikált cikkek vannak (szűrve,
// cache mögött, feloldott asset-URL-ekkel). Itt a NYERS busz-sor kell — minden státusszal, a
// markdown eredeti `{asset base}/{fileId}` hivatkozásaival és a `version`-nel, ami az OCC-mentés
// bemenete. A `version` a felhasználónál töltött állapotot azonosítja: ha közben más mentett,
// a mentés 409-cel elhal, sosem írjuk felül csendben a másik munkáját.

export const EditorArticleSchema = z.object({
  id: z.string(),
  /** OCC-token: ezt küldi vissza a mentés `version` mezőjében a kliens. */
  version: z.number(),
  categoryId: z.string(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  /** Nyers markdown — asset-feloldás NÉLKÜL (a szerkesztő az eredeti hivatkozásokat őrzi meg). */
  body: z.string(),
  /**
   * MEGJELENÍTÉSI segédtérkép a törzs kép-hivatkozásaihoz: `{ASSET_BASE}/{fileId}` → presigned URL.
   *
   * Miért külön mező, és miért NEM a `body`-ba írjuk: a szerkesztő UGYANEZT a `body`-t küldi vissza
   * mentésre, tehát egy feloldott — néhány perc alatt LEJÁRÓ — presigned URL a törzsben tartósan
   * elrontaná a cikket. Így a tárolt tartalom marad tár-független, a megjelenítés pedig ebből a
   * térképből dolgozik (a vizuális szerkesztő szerializáláskor visszafordítja a hivatkozásra).
   *
   * Csak a BETÖLTÉSI út (`editorArticle`) tölti ki: a mentés/áthelyezés válaszában nincs értelme,
   * mert a kliens a már felépített térképét használja tovább — ezért opcionális.
   */
  assetUrls: z.record(z.string()).optional(),
  tags: z.array(z.string()),
  status: z.enum(ARTICLE_STATUSES),
  visibility: z.enum(VISIBILITIES),
  updatedAt: z.string(),
  /** Útvonal-kontextus a kenyérmorzsához; `null`, ha a kategória/terület közben eltűnt. */
  path: z
    .object({
      spaceSlug: z.string(),
      spaceName: z.string(),
      categorySlug: z.string(),
      categoryName: z.string(),
      /**
       * A SZÜLŐ szintek nyilvánossága — enélkül a szerkesztő nem tudja megmondani, hogy a cikk
       * nyilvánosra állítása egyáltalán ki tud-e látszani a súgóra.
       *
       * MIÉRT KELL (élő eset, 2026-08-24): a szerkesztő a cikk SAJÁT `visibility` mezőjéből írt ki
       * „Nyilvános" címkét, miközben a publikus közönség a hierarchia MINDHÁROM szintjét szűri.
       * Élesben egyetlen szekció sem volt nyilvános, tehát a felület azt állította, hogy a tartalom
       * kikerült — holott semmi nem került ki, és ezt semmi nem jelezte.
       *
       * A `categoryPubliclyVisible` EFFEKTÍV érték (szekció ∧ könyv), nem a könyv saját mezője: egy
       * belső szekcióban álló „nyilvános" könyv itt `false`.
       */
      spacePubliclyVisible: z.boolean(),
      categoryPubliclyVisible: z.boolean(),
    })
    .nullable(),
});
export type EditorArticle = z.infer<typeof EditorArticleSchema>;

/**
 * A cikk-törzs explicit max hossza (audit T16). A Fastify 1 MiB nyers body-limitje véd a
 * szervert, de egy azon ÁTCSÚSZÓ, ám túl nagy törzsnél néma 413-at adna a felhasználónak — ez a
 * korlát BELÜL van a bájt-limiten, hogy a Zod-validáció (beszédes 400, lásd
 * `apps/api/src/modules/editor/article-save-limits.spec.ts`) mindig előbb érjen oda.
 *
 * Aritmetika: a Zod `.max()` a JS string UTF-16 hosszát nézi. A LEGROSSZABB UTF-8 bájt/egység
 * arány 3 (a Latin-1-en túli, de nem szürrogát-páros BMP-karakterek, pl. CJK), nem a Hungarian
 * ékezetes betűk 2-es aránya. 200 000 karakter × 3 bájt = 600 000 bájt; a többi mező (title 300,
 * summary 1000, changeNote 500, 30×60 tag) legrosszabb esetben is csak ~11 000 bájt — összesen
 * ~611 000 bájt, ~42%-os tartalékkal az 1 MiB (1 048 576 bájt) alatt még a legrosszabb kódolású
 * tartalomnál is. A valós medián cikk ~4000 karakter — ez a korlát annak ~50-szerese.
 */
export const MAX_ARTICLE_BODY_LENGTH = 200_000;

export const SaveArticleBodySchema = z.object({
  title: z.string().min(1).max(300),
  summary: z.string().max(1000),
  body: z.string().max(MAX_ARTICLE_BODY_LENGTH),
  tags: z.array(z.string().min(1).max(60)).max(30),
  /** A revízióhoz kerülő rövid indoklás („mit változtattam"). */
  changeNote: z.string().max(500).optional(),
  /** A betöltött állapot verziója (OCC) — eltérésnél 409. */
  version: z.number().int().min(0),
});
export type SaveArticleBody = z.infer<typeof SaveArticleBodySchema>;

/**
 * Áthelyezés másik könyvbe. A pozíciót NEM tartalmazza: a cikk a cél-könyv végére kerül, és ha a
 * felhasználó a lista közepére húzta, a felület egy külön `reorder`-rel rendezi a cél-könyvet. Így a
 * pozíció-logika egy helyen él (a reorder-végpontokban), nem két félig-átfedő szerződésben.
 */
export const MoveArticleBodySchema = z.object({
  categoryId: z.string().uuid(),
  /** A betöltött állapot verziója (OCC) — eltérésnél 409. */
  version: z.number().int().min(0),
});
export type MoveArticleBody = z.infer<typeof MoveArticleBodySchema>;

/** 409-válasz: a szerkesztő megmutathatja, mi a jelenlegi állapot, és mit írt volna felül. */
export const SaveConflictSchema = z.object({
  message: z.string(),
  current: EditorArticleSchema,
});
export type SaveConflict = z.infer<typeof SaveConflictSchema>;

/**
 * Autosave-vázlat. Az Odinban **selfScoped** típus: a szerző a hívó identity — ezért a vázlatot
 * a felhasználó tokenjével írja a BFF, nem a service-identityvel (különben minden vázlat a
 * service-é lenne). Egy cikkhez felhasználónként egy vázlat tartozik.
 */
export const DraftSchema = z.object({
  id: z.string(),
  articleId: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  savedAt: z.string(),
});
export type Draft = z.infer<typeof DraftSchema>;

export const SaveDraftBodySchema = z.object({
  title: z.string().max(300),
  /** Ugyanaz a korlát, mint a végleges mentésnél (`MAX_ARTICLE_BODY_LENGTH`) — a vázlat ugyanazt
   * a törzset hordozza, ugyanaz az opaque-413 kockázat vonatkozik rá. */
  body: z.string().max(MAX_ARTICLE_BODY_LENGTH),
});
export type SaveDraftBody = z.infer<typeof SaveDraftBodySchema>;

// ── Revíziók (F4b) ──
//
// A revíziók append-only naplót alkotnak: visszaállításkor sem írjuk felül őket, hanem a mentés
// előtti állapotról készül új bejegyzés `restore` jelöléssel. A lista TÖRZS NÉLKÜL jön (1977
// revízió × több KB-os törzs kezelhetetlen lenne); a tartalom a konkrét revízió megnyitásakor.

export const RevisionSummarySchema = z.object({
  id: z.string(),
  revision: z.number(),
  title: z.string(),
  changeNote: z.string(),
  authorId: z.string(),
  /** Feloldott név; üres, ha az identity-lista nem elérhető (lásd IdentityService). */
  authorName: z.string(),
  source: z.enum(REVISION_SOURCES),
  createdAt: z.string(),
});
export type RevisionSummary = z.infer<typeof RevisionSummarySchema>;

export const RevisionDetailSchema = RevisionSummarySchema.extend({
  body: z.string(),
  summary: z.string(),
});
export type RevisionDetail = z.infer<typeof RevisionDetailSchema>;

// ── Csatolmányok (F4c) ──
//
// Háromlépéses flow (building-on-odin §15): a BFF kér presigned feltöltő-URL-t, a BÖNGÉSZŐ tölt fel
// közvetlenül az objektum-tárba, majd a BFF véglegesít és rögzíti a csatolmány-sort. A bájtok soha
// nem mennek át az app-backenden.

/** Az Odin default korlátja 50 MiB; itt szűkebben szabunk, hogy a felület előre jelezhessen. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export const AttachmentSchema = z.object({
  id: z.string(),
  fileId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  kind: z.enum(['image', 'attachment']),
  createdAt: z.string(),
  /** A cikk törzsébe illesztendő, tár-független hivatkozás (`{ASSET_BASE}/{fileId}`). */
  reference: z.string(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/**
 * A VÉGLEGESÍTÉS (commit) válasza — a csatolmány + egy CSAK-OLVASÓ megjelenítési URL.
 *
 * Miért KÜLÖN séma, és miért itt van az egyetlen megjelenítési URL: a feltöltés három lépéséből a
 * MÁSODIK egy presigned **PUT** jegy, ami ÍRÁS-KÉPES hitelesítő adat (amíg érvényes, felülírható
 * vele az objektum bájtjai). Az azonnali előnézetet korábban EZ a jegy adta — vagyis egy írás-képes
 * URL ült a megjelenítési state-ben, és a DOM-ba is `<img src>`-ként renderelődött. A helyes cím a
 * commit UTÁN keletkező, on-behalf feloldott LETÖLTŐ URL, és a szerződés ezt a KÜLÖN sémát azért
 * kapja, hogy a kettő ne legyen felcserélhető: a `displayUrl` a válaszban él, a feltöltő-jegy pedig
 * SOHA nem kerül ki abból a függvényből, ami a bájtokat felteszi (lásd `use-upload.ts`).
 *
 * Opcionális: ha a feloldás nem sikerül (törölt fájl, elérhetetlen tár), a kliens a tár-független
 * `reference`-nél marad — pontosan úgy, mint a migrált tartalomnál. Nem adunk ki „valamilyen" URL-t.
 */
export const CommittedAttachmentSchema = AttachmentSchema.extend({
  displayUrl: z.string().optional(),
});
export type CommittedAttachment = z.infer<typeof CommittedAttachmentSchema>;

export const UploadUrlBodySchema = z.object({
  filename: z.string().min(1).max(255),
  // A típus-kapu a `@morgens-hub/shared` EGYETLEN listájából jön (nincs duplikált halmaz). A
  // presigned letöltést az objektum-tár a feltöltéskor deklarált `Content-Type`-pal szolgálja ki,
  // ezért egy `text/html` vagy SVG feltöltés tárolt szkript lenne a tár doménjén.
  contentType: z
    .string()
    .min(1)
    .max(255)
    .refine(isAllowedUploadType, { message: 'Ez a fájltípus nem tölthető fel.' }),
  sizeBytes: z.number().int().positive().max(MAX_ATTACHMENT_BYTES),
});
export type UploadUrlBody = z.infer<typeof UploadUrlBodySchema>;

export const UploadTicketSchema = z.object({
  fileId: z.string(),
  uploadUrl: z.string(),
  expiresIn: z.number(),
});
export type UploadTicket = z.infer<typeof UploadTicketSchema>;

// ── Új cikk (F4e) ──
//
// A cikk MINDIG vázlatként születik: az írás első pillanatában senki sem akarja, hogy a fél mondat
// már kint legyen. A slug a címből képződik, az ütközést a szerver oldja fel (a kategórián belül
// egyedi) — a szerzőnek nem kell URL-eket kitalálnia.

export const CreateArticleBodySchema = z.object({
  categoryId: z.string().uuid(),
  title: z.string().min(1).max(300),
  summary: z.string().max(1000).optional(),
  /**
   * Kezdő törzs — a választott CIKK-VÁZ tartalma (2026-08-27). Enélkül a váz csak egy második,
   * mentés-körrel kerülhetne a cikkbe, és egy megszakadt folyamat üres cikket hagyna maga után.
   */
  body: z.string().max(200_000).optional(),
});
export type CreateArticleBody = z.infer<typeof CreateArticleBodySchema>;

// ── Szerkezet: területek és kategóriák (F4e) ──

export const StructureCategorySchema = z.object({
  id: z.string(),
  version: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  position: z.number(),
  archived: z.boolean(),
  visibility: z.enum(VISIBILITIES),
  articleCount: z.number(),
  /**
   * A kukában lévő (`deleted`) cikkek száma — KÜLÖN az `articleCount`-tól.
   *
   * A törlés-blokkoláshoz kell: a kuka csak app-szintű jelölés, a busz-sor létezik, tehát az Odin
   * FK-ja a kukás cikket is akadálynak látja. A felület így előre meg tudja mondani, mi blokkol.
   */
  trashedCount: z.number(),
});
export type StructureCategory = z.infer<typeof StructureCategorySchema>;

export const StructureSpaceSchema = z.object({
  id: z.string(),
  version: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  position: z.number(),
  archived: z.boolean(),
  /** `internal` | `public` — a publikus súgó a hierarchia MINDHÁROM szintjét nézi. */
  visibility: z.enum(VISIBILITIES),
  categories: z.array(StructureCategorySchema),
});
export type StructureSpace = z.infer<typeof StructureSpaceSchema>;

export const StructureSchema = z.object({ spaces: z.array(StructureSpaceSchema) });

/**
 * Átrendezés: a szülő gyermekeinek azonosítói a kívánt sorrendben. A szerver 0..n-1-re normalizálja
 * a `position` mezőket.
 *
 * A lista lehet RÉSZHALMAZ: a felület nem látja a könyv összes cikkét (az olvasó végpont csak a
 * publikáltakat adja). A fel nem sorolt testvérek a felsoroltak UTÁN kapnak pozíciót, egymáshoz
 * képest változatlan sorrendben. Idegen azonosító viszont 400.
 */
export const ReorderBodySchema = z.object({ ids: z.array(z.string().uuid()).min(1) });
export type ReorderBody = z.infer<typeof ReorderBodySchema>;

/**
 * Kuka: a `deleted` státuszú cikkek. Külön séma, mert itt más kérdésre válaszolunk, mint a
 * szerkezetnél — nem az, hogy „mi hol van", hanem hogy „mit dobtak ki, és honnan".
 */
export const TrashArticleSchema = z.object({
  id: z.string(),
  version: z.number(),
  slug: z.string(),
  title: z.string(),
  updatedAt: z.string(),
  /** Ahonnan kikerült; üres, ha a kategória időközben eltűnt. */
  spaceName: z.string(),
  categoryName: z.string(),
});
export const TrashSchema = z.object({ articles: z.array(TrashArticleSchema) });
export type Trash = z.infer<typeof TrashSchema>;
export type Structure = z.infer<typeof StructureSchema>;

export const SpaceInputSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional(),
  /**
   * A terület megjelenése `"<ikon>:<szín>"` alakban (lásd `@morgens-hub/shared` appearance).
   * A validáció itt van, hogy a buszra ne kerülhessen értelmezhetetlen érték — a régi, importált
   * tartalom szabad szövegét viszont csak a felület hagyja figyelmen kívül, nem utasítja vissza.
   */
  icon: z.string().max(60).refine(isValidAppearance, 'Ismeretlen ikon vagy szín.').optional(),
  /** Csak módosításnál: archiválás/visszahozás. */
  archived: z.boolean().optional(),
});
export type SpaceInput = z.infer<typeof SpaceInputSchema>;

export const CategoryInputSchema = SpaceInputSchema.omit({ icon: true }).extend({
  spaceId: z.string().uuid(),
});
export type CategoryInput = z.infer<typeof CategoryInputSchema>;

/** Módosításnál OCC-verzió is kell (mint mindenhol, ahol felülírás történhet). */
export const UpdateSpaceBodySchema = SpaceInputSchema.extend({ version: z.number().int().min(0) });
export const UpdateCategoryBodySchema = CategoryInputSchema.extend({
  version: z.number().int().min(0),
});

// ── Életciklus (F4d) ──
//
// Státuszonként KÜLÖN végpont, nem egy „állítsd erre" művelet: minden átmenetnek saját joga van
// (`article.publish` / `unpublish` / `archive` / `delete`), és így a jog-kapu a guardban marad,
// nem a törzs tartalmától függő elágazásban.
//
// A törlés **kukába** tesz (`status: 'deleted'`), nem a buszról töröl — így visszaállítható.

export const LifecycleBodySchema = z.object({
  /** A betöltött verzió (OCC) — eltérésnél 409, mint a mentésnél. */
  version: z.number().int().min(0),
});
export type LifecycleBody = z.infer<typeof LifecycleBodySchema>;

export const RestoreRevisionBodySchema = z.object({
  /** A cikk betöltött verziója (OCC) — eltérésnél 409, mint a mentésnél. */
  version: z.number().int().min(0),
});
export type RestoreRevisionBody = z.infer<typeof RestoreRevisionBodySchema>;

// ── Root contract ──

/** Az Odin- és más upstream szolgáltatáshibák közös végponti palettája. */
const UpstreamErrorResponses = {
  502: ErrorSchema,
  503: ErrorSchema,
} as const;

export const hubContract = c.router({
  health: {
    method: 'GET',
    path: '/health',
    responses: {
      // Additív: a régi `{ status: 'ok' }` alak (Coolify/Mimir + E2E) változatlan marad, csak
      // új mezők jönnek hozzá. A próbák eredménye szándékosan csak enum — nyers hibaüzenet,
      // hostnév vagy verziószám sose kerül a publikus, auth nélküli válaszba.
      200: z.object({
        status: z.literal('ok'),
        /** Összesített jelzés — az Odin-elérés a döntő; a keresőindex hiánya (dev-envben
         *  gyakori, `INDEX_DATABASE_URL` nélkül) NEM teszi „nem-ok"-ká a szolgáltatást. */
        ok: z.boolean(),
        odin: z.enum(['ok', 'unreachable']),
        index: z.enum(['ok', 'off', 'error']),
        /** A sablon-típus séma-regisztrációjának BOOT-eredménye (2026-08-28). Enum, mert a
         *  `/health` anonim: `ok` = a típus regisztrálva; `skipped` = nincs sync-kredencia;
         *  `rejected` = az Odin elutasította (tipikusan 403, hiányzó `knowledge:admin`);
         *  `error` = a hívás elszállt; `pending` = a boot-lépés még nem futott le. */
        templates: z.enum(['ok', 'skipped', 'rejected', 'error', 'pending']),
        /** A legutóbbi valódi sablon-lista-kérés eredménye: `unknown` = a boot óta nem volt
         *  ilyen kérés; `missing` = a busz nem ismeri a típust; `denied` = a felhasználó tokene
         *  nem olvashatja; `unavailable` = upstream 5xx. */
        templatesList: z.enum(['unknown', 'ok', 'missing', 'denied', 'unavailable', 'other']),
        /** A legutóbbi sablon-lista-kérés nyers upstream HTTP-státusza (0 = nem volt ilyen kérés). */
        templatesListStatus: z.number().int().nonnegative(),
        /** A legutóbbi sablon-létrehozás nyers upstream státusza (0 = nem volt ilyen kérés). */
        templatesWriteStatus: z.number().int().nonnegative(),
        /** A módosítás két lépésének nyers upstream státusza: olvasás, majd írás. */
        templatesReadStatus: z.number().int().nonnegative(),
        templatesUpdateStatus: z.number().int().nonnegative(),
        uptimeSec: z.number().int().nonnegative(),
      }),
    },
    summary: 'Életjel — LB-healthcheckhez, auth nélkül. Beszédes: Odin-elérés + keresőindex-állapot.',
  },
  login: {
    method: 'POST',
    path: '/auth/login',
    body: LoginBodySchema,
    responses: {
      200: z.object({ accessToken: z.string() }),
      401: ErrorSchema,
      429: ErrorSchema,
      503: ErrorSchema,
    },
    summary:
      'Bejelentkezés — az Odin iam login proxyja (app: morgens-hub); access-tokent ad vissza. A 429 a rate-limitet jelzi (audit C1/M8) — a `@fastify/rate-limit` globálisan regisztrált plugin adja, a contract csak dokumentálja a kliens felé.',
  },
  refresh: {
    method: 'POST',
    path: '/auth/refresh',
    body: z.object({}).optional(),
    responses: {
      200: z.object({ accessToken: z.string() }),
      401: ErrorSchema,
      503: ErrorSchema,
    },
    summary:
      'Access-token megújítás a httpOnly refresh-cookie-ból — a cookie-t rotálja; érvénytelen/hiányzó cookie → 401 (a cookie-t törli); Odin-kiesésnél 503 (a cookie ÉRINTETLEN marad).',
  },
  logout: {
    method: 'POST',
    path: '/auth/logout',
    body: z.object({}).optional(),
    responses: {
      200: z.object({ ok: z.boolean() }),
    },
    summary: 'Kijelentkezés — a refresh-token visszavonása az Odin iam-ben + a cookie törlése.',
  },
  session: {
    method: 'GET',
    path: '/session',
    responses: {
      200: SessionSchema,
      401: ErrorSchema,
    },
    summary: 'A bejelentkezett identity (a JWKS-validált token claimjei).',
  },
  // A JOG-KATALÓGUS ember-olvasható címkéi. A forrás a Hub SAJÁT manifestje
  // (`rbac-manifest.ts`) — ugyanaz, amit a BFF induláskor az Odinba szinkronizál. Így a felület
  // nem tart MÁSODIK címke-szótárt (a repó szabálya ezt tiltja), és egy új jog a manifest
  // bővítésével magától megjelenik a profilon is.
  permissionCatalog: {
    method: 'GET',
    path: '/permissions/catalog',
    responses: {
      200: PermissionCatalogSchema,
      401: ErrorSchema,
    },
    summary: 'A jogosultságok ember-olvasható nevei és csoportjai (a Hub manifestjéből).',
  },
  knowledgeTree: {
    method: 'GET',
    path: '/knowledge/tree',
    responses: {
      200: KnowledgeTreeSchema,
      401: ErrorSchema,
    },
    summary: 'A tudástár navigációs fája (területek → kategóriák → cikk-előnézetek).',
  },
  knowledgeSpace: {
    method: 'GET',
    path: '/knowledge/spaces/:spaceSlug',
    pathParams: z.object({ spaceSlug: z.string() }),
    responses: {
      200: SpacePageSchema,
      401: ErrorSchema,
      404: ErrorSchema,
    },
    summary: 'Egy terület kategóriái a cikkeikkel — a terület-oldal és a fa aktív ága.',
  },
  knowledgeRecent: {
    method: 'GET',
    path: '/knowledge/recent',
    responses: {
      200: RecentArticlesSchema,
      401: ErrorSchema,
    },
    summary: 'A legutóbb frissített cikkek (kezdőlap-belépő).',
  },
  search: {
    method: 'GET',
    path: '/search',
    query: z.object({
      q: z.string(),
      spaceSlug: z.string().optional(),
      tag: z.string().optional(),
      scope: SearchScopeSchema.optional(),
      limit: z.coerce.number().int().min(1).max(50).optional(),
    }),
    responses: {
      200: SearchResultSchema,
      401: ErrorSchema,
    },
    summary: 'Keresés a tudástárban (magyar FTS; a cserélhető retrieval-kontrakt mögött).',
  },
  indexStatus: {
    method: 'GET',
    path: '/search/index',
    responses: {
      200: IndexStatusSchema,
      401: ErrorSchema,
      403: ErrorSchema,
    },
    summary: 'A keresőindex állapota (adminisztráció).',
  },
  indexRebuild: {
    method: 'POST',
    path: '/search/index/rebuild',
    body: z.object({}).optional(),
    responses: {
      200: z.object({ articles: z.number(), chunks: z.number(), durationMs: z.number() }),
      401: ErrorSchema,
      403: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'A keresőindex teljes újraépítése (adminisztráció) — az index eldobható melléktermék.',
  },
  searchMisses: {
    method: 'GET',
    path: '/search/misses',
    query: z.object({
      limit: z.coerce.number().int().min(1).max(200).optional(),
    }),
    responses: {
      200: SearchMissesSchema,
      401: ErrorSchema,
      403: ErrorSchema,
    },
    summary:
      'A leggyakrabban hiába keresett kifejezések (adminisztráció) — mit nem talál meg a tudástár.',
  },
  knowledgeArticle: {
    method: 'GET',
    path: '/knowledge/articles/:spaceSlug/:categorySlug/:articleSlug',
    pathParams: z.object({
      spaceSlug: z.string(),
      categorySlug: z.string(),
      articleSlug: z.string(),
    }),
    responses: {
      200: ArticlePageSchema,
      401: ErrorSchema,
      404: ErrorSchema,
    },
    summary:
      'Cikk útvonal szerint — alias-slugra is felold (a válasz canonical mezője alapján a kliens átirányít).',
  },
  articleComments: {
    method: 'GET',
    path: '/knowledge/articles/:articleId/comments',
    pathParams: z.object({ articleId: z.string().uuid() }),
    responses: {
      200: CommentsSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Egy cikk kommentjei, időrendben (belső felület — a súgó nem adja).',
  },
  commentCreate: {
    method: 'POST',
    path: '/knowledge/articles/:articleId/comments',
    pathParams: z.object({ articleId: z.string().uuid() }),
    body: CommentInputSchema,
    responses: {
      201: CommentSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Új komment vagy válasz (`comment.add` — a member tier is kommentelhet).',
  },
  commentResolve: {
    method: 'POST',
    path: '/comments/:id/resolve',
    pathParams: z.object({ id: z.string().uuid() }),
    body: z.object({ version: z.number().int().min(0), resolved: z.boolean() }),
    responses: {
      200: CommentSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Szál lezárása / újranyitása (`comment.moderate`).',
  },
  commentDelete: {
    method: 'DELETE',
    path: '/comments/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    body: z.object({}).optional(),
    responses: {
      200: z.object({ ok: z.boolean() }),
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary:
      'Komment törlése — a SAJÁT kommentet a szerző is törölheti, a másokét `comment.moderate`.',
  },
  knowledgePopularTags: {
    method: 'GET',
    path: '/knowledge/tags',
    query: z.object({ limit: z.coerce.number().int().min(1).max(24).optional() }),
    responses: {
      200: PopularTagsSchema,
      401: ErrorSchema,
    },
    summary: 'A leggyakoribb címkék darabszámmal — a kezdőlap belépő-chipjei.',
  },
  knowledgeTag: {
    method: 'GET',
    path: '/knowledge/tags/:tag',
    pathParams: z.object({ tag: z.string() }),
    responses: {
      200: TagPageSchema,
      401: ErrorSchema,
    },
    summary: 'Egy címke összes cikke, terület szerint csoportosítva (ismeretlen címke: üres lista).',
  },
  editorArticle: {
    method: 'GET',
    path: '/editor/articles/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
    },
    summary: 'Egy cikk szerkesztésre — nyers törzs, minden státusz, OCC-verzióval.',
  },
  editorSaveArticle: {
    method: 'PUT',
    path: '/editor/articles/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    body: SaveArticleBodySchema,
    responses: {
      200: EditorArticleSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Cikk mentése — előbb revízió a jelenlegi állapotról, utána OCC-írás (ütközés: 409).',
  },
  editorMoveArticle: {
    method: 'POST',
    path: '/editor/articles/:id/move',
    pathParams: z.object({ id: z.string().uuid() }),
    body: MoveArticleBodySchema,
    responses: {
      200: EditorArticleSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary:
      'Cikk áthelyezése másik könyvbe (`article.manage`, OCC). A cél-könyv VÉGÉRE kerül; a slug a cél-könyvhöz képest deduplikálódik, a régi slug alias lesz.',
  },
  editorDraft: {
    method: 'GET',
    path: '/editor/articles/:id/draft',
    pathParams: z.object({ id: z.string().uuid() }),
    responses: {
      200: z.object({ draft: DraftSchema.nullable() }),
      401: ErrorSchema,
      403: ErrorSchema,
    },
    summary: 'A felhasználó saját, mentetlen vázlata ehhez a cikkhez (ha van).',
  },
  editorSaveDraft: {
    method: 'PUT',
    path: '/editor/articles/:id/draft',
    pathParams: z.object({ id: z.string().uuid() }),
    body: SaveDraftBodySchema,
    responses: {
      200: DraftSchema,
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Vázlat automatikus mentése (selfScoped — a saját tokennel, a szerző a hívó).',
  },
  editorRevisions: {
    method: 'GET',
    path: '/editor/articles/:id/revisions',
    pathParams: z.object({ id: z.string().uuid() }),
    responses: {
      200: z.object({ revisions: z.array(RevisionSummarySchema) }),
      401: ErrorSchema,
      403: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'A cikk revízió-naplója, legfrissebb elöl (törzs nélkül).',
  },
  editorRevision: {
    method: 'GET',
    path: '/editor/articles/:id/revisions/:revisionId',
    pathParams: z.object({ id: z.string().uuid(), revisionId: z.string().uuid() }),
    responses: {
      200: RevisionDetailSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Egy revízió teljes tartalma (az összehasonlításhoz és a visszaállításhoz).',
  },
  editorRestoreRevision: {
    method: 'POST',
    path: '/editor/articles/:id/revisions/:revisionId/restore',
    pathParams: z.object({ id: z.string().uuid(), revisionId: z.string().uuid() }),
    body: RestoreRevisionBodySchema,
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary:
      'Revízió visszaállítása — a jelenlegi állapotról `restore` jelöléssel revízió készül, a napló nem íródik felül.',
  },
  // ── Publikus súgó (F6) — BEJELENTKEZÉS NÉLKÜL ──
  // Ezek az egyetlen végpontok, amelyekhez nem kell token. A BFF a service-identityvel olvas, és
  // KIZÁRÓLAG a `visibility: 'public'` halmazt adja (lásd Audience a knowledge-query service-ben).
  publicTree: {
    method: 'GET',
    path: '/public/knowledge/tree',
    responses: { 200: KnowledgeTreeSchema, 503: ErrorSchema },
    summary: 'A publikus súgó tartalomjegyzéke (auth nélkül).',
  },
  publicSpace: {
    method: 'GET',
    path: '/public/knowledge/spaces/:spaceSlug',
    pathParams: z.object({ spaceSlug: z.string() }),
    responses: { 200: SpacePageSchema, 404: ErrorSchema, 503: ErrorSchema },
    summary: 'Publikus terület a cikkeivel (auth nélkül).',
  },
  publicArticle: {
    method: 'GET',
    path: '/public/knowledge/articles/:spaceSlug/:categorySlug/:articleSlug',
    pathParams: z.object({
      spaceSlug: z.string(),
      categorySlug: z.string(),
      articleSlug: z.string(),
    }),
    responses: { 200: ArticlePageSchema, 404: ErrorSchema, 503: ErrorSchema },
    summary: 'Publikus cikk (auth nélkül).',
  },
  publicSearch: {
    method: 'GET',
    path: '/public/search',
    query: z.object({
      q: z.string(),
      scope: SearchScopeSchema.optional(),
      limit: z.coerce.number().int().min(1).max(30).optional(),
    }),
    responses: { 200: SearchResultSchema, 503: ErrorSchema },
    summary:
      'Keresés a NYILVÁNOS cikkek közt (auth nélkül) — az index effektív láthatóság-mezője szűr.',
  },
  editorCreateArticle: {
    method: 'POST',
    path: '/editor/articles',
    body: CreateArticleBodySchema,
    responses: {
      201: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Új cikk létrehozása vázlatként (a slug a címből, ütközés-feloldással).',
  },
  // ── Sablonok ──
  //
  // OLVASÁS: `article.manage` — aki cikket szerkeszt, lát sablont. ÍRÁS: `template.manage`
  // (admin+superadmin). A törlés ARCHIVÁLÁS: a sablon eltűnik a felajánlásból, de a története
  // megmarad (ugyanaz az elv, mint a szekció/könyv archiválásánál).
  editorTemplates: {
    method: 'GET',
    path: '/editor/templates',
    query: z.object({
      /** Szűrés fajtára — a szerkesztő oldalsáv csak `snippet`-et kér, az új-cikk lap `article`-t. */
      kind: TemplateKindSchema.optional(),
    }),
    responses: {
      200: TemplatesSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Sablonok listája (az archiváltak nélkül).',
  },
  editorCreateTemplate: {
    method: 'POST',
    path: '/editor/templates',
    body: TemplateInputSchema,
    responses: {
      201: TemplateSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Új sablon létrehozása.',
  },
  editorUpdateTemplate: {
    method: 'PUT',
    path: '/editor/templates/:id',
    body: TemplateUpdateSchema,
    responses: {
      200: TemplateSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Sablon módosítása vagy archiválása (OCC a `version` mezőből).',
  },
  editorStructure: {
    method: 'GET',
    path: '/editor/structure',
    responses: {
      200: StructureSchema,
      401: ErrorSchema,
      403: ErrorSchema,
    },
    summary: 'A teljes szerkezet szerkesztéshez — az archivált területek/kategóriák is.',
  },
  editorCreateSpace: {
    method: 'POST',
    path: '/editor/spaces',
    body: SpaceInputSchema,
    responses: { 201: StructureSpaceSchema, 401: ErrorSchema, 403: ErrorSchema, 503: ErrorSchema },
    summary: 'Új terület (`space.manage`).',
  },
  editorUpdateSpace: {
    method: 'PUT',
    path: '/editor/spaces/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    body: UpdateSpaceBodySchema,
    responses: {
      200: StructureSpaceSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'Szekció módosítása vagy archiválása (`space.manage`, OCC).',
  },
  /**
   * Szekció törlése — CSAK ÜRESEN, `space.delete` joggal (superadmin tier).
   *
   * Nincs OCC: a busz-remove nem támogat `If-Match`-et. Az ürességi ellenőrzés a szerveren, a
   * törlés előtt fut; ha a busz mégis nemet mond (közben tartalom került bele), az 400.
   */
  editorDeleteSpace: {
    method: 'DELETE',
    path: '/editor/spaces/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    // Üres body: a ts-rest csak így ad mutation-hookot DELETE-re (lásd editorDeleteDraft).
    body: z.object({}).optional(),
    responses: {
      200: z.object({ ok: z.boolean() }),
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Szekció törlése — csak üresen (`space.delete`).',
  },
  editorCreateCategory: {
    method: 'POST',
    path: '/editor/categories',
    body: CategoryInputSchema,
    responses: {
      201: StructureCategorySchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'Új kategória egy területen belül (`category.manage`).',
  },
  /**
   * A terület láthatósága — KÜLÖN végponton, `article.publish_public` joggal.
   *
   * MIÉRT nem a sima szerkesztésben: a nyilvánosság ugyanaz a döntés terület-, kategória- és
   * cikk-szinten („kikerül-e a cégen kívülre"), tehát ugyanaz a jog védje. Ha a mezőt a szerkesztő
   * végpont is elfogadná, egy `space.manage` jogú admin megkerülné a kaput.
   */
  editorSpaceVisibility: {
    method: 'POST',
    path: '/editor/spaces/:id/visibility',
    pathParams: z.object({ id: z.string().uuid() }),
    body: z.object({ version: z.number().int().min(0), visibility: z.enum(VISIBILITIES) }),
    responses: {
      200: StructureSpaceSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'A terület nyilvánossá / belsővé tétele (a publikus súgó hierarchia-kapuja).',
  },
  /**
   * A KÖNYV publikált cikkeinek kitétele a súgóra, egy kéréssel.
   *
   * MIÉRT VAN EGYÁLTALÁN (2026-08-24, projekt-tulajdonosi kérés): a nyilvánosság hierarchikus, tehát
   * a könyv nyilvánossá tétele UTÁN minden cikket egyenként is át kell állítani — 88 cikkes könyvnél
   * ez nem munka, hanem elrettentés. **NEM automatikus kaszkád:** a publikálás cikk-szintű bizalmi
   * döntés (egy könyvben lehet belső jegyzet), és egy néma átterjedés utólag észrevehetetlenül tenne
   * ki tartalmat. Ezt a végpontot a felület KIMONDOTT megerősítés után hívja, a darabszámmal.
   *
   * SZIGORÍTÁSOK a szerveren (nem a felület fegyelmén):
   *  · CSAK `published` státuszú cikk — vázlat és archivált SOHA. A vázlatot a publikus fal ma is
   *    kizárja, de a `public` mező beírása időzített bomba lenne: a publikálás pillanatában, döntés
   *    nélkül kimenne.
   *  · a már nyilvános sorokat nem írja újra (nincs értelmetlen revízió),
   *  · NEM terjed lefelé a szekcióról: 96 könyv egyszerre nem egykattintásos művelet.
   *
   * A válasz azért ad darabszámokat és hiba-listát, mert a művelet NEM ATOMI (N busz-írás): a
   * részleges bukásnak látszania kell, nem néma csonkolásként (ugyanaz az elv, mint a `reorder`-nél).
   */
  editorPublishCategoryArticles: {
    method: 'POST',
    path: '/editor/categories/:id/publish-articles',
    pathParams: z.object({ id: z.string().uuid() }),
    body: z.object({}),
    responses: {
      200: z.object({
        /** Ahány `published` státuszú cikk a könyvben van (a művelet célhalmaza). */
        candidates: z.number(),
        /** Ahányat MOST tettünk nyilvánossá. */
        published: z.number(),
        /** Ahány már korábban nyilvános volt — nem írtuk újra. */
        alreadyPublic: z.number(),
        failures: z.array(
          z.object({
            id: z.string(),
            title: z.string(),
            /** `conflict` = OCC-ütközés (időközben más írta), `error` = busz-hiba. */
            reason: z.enum(['conflict', 'error']),
          }),
        ),
      }),
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'A könyv publikált cikkeinek kitétele a súgóra (`article.publish_public`).',
  },
  editorCategoryVisibility: {
    method: 'POST',
    path: '/editor/categories/:id/visibility',
    pathParams: z.object({ id: z.string().uuid() }),
    body: z.object({ version: z.number().int().min(0), visibility: z.enum(VISIBILITIES) }),
    responses: {
      200: StructureCategorySchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'A kategória nyilvánossá / belsővé tétele.',
  },
  editorUpdateCategory: {
    method: 'PUT',
    path: '/editor/categories/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    body: UpdateCategoryBodySchema,
    responses: {
      200: StructureCategorySchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'Könyv módosítása, archiválása vagy áthelyezése (`category.manage`, OCC).',
  },
  /** Könyv törlése — csak üresen, a kukás cikkeket is akadálynak véve (`category.delete`). */
  editorDeleteCategory: {
    method: 'DELETE',
    path: '/editor/categories/:id',
    pathParams: z.object({ id: z.string().uuid() }),
    body: z.object({}).optional(),
    responses: {
      200: z.object({ ok: z.boolean() }),
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Könyv törlése — csak üresen (`category.delete`).',
  },
  /**
   * A sorrend KÉT végponton megy, nem egy generikuson: a jog más (`category.manage` vs
   * `article.manage`), és egy `{ type, ids }` alakú végponton a guard nem tudná eldönteni, mit
   * kérjen — a döntés a service-be csúszna, a deny-by-default minta sérülne.
   */
  editorReorderCategories: {
    method: 'POST',
    path: '/editor/spaces/:id/categories/reorder',
    pathParams: z.object({ id: z.string().uuid() }),
    body: ReorderBodySchema,
    responses: {
      200: z.object({ ok: z.literal(true) }),
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary:
      'A szekció könyveinek sorrendje (`category.manage`). Az `ids` lehet részhalmaz — a fel nem soroltak a lista után kerülnek.',
  },
  editorReorderArticles: {
    method: 'POST',
    path: '/editor/categories/:id/articles/reorder',
    pathParams: z.object({ id: z.string().uuid() }),
    body: ReorderBodySchema,
    responses: {
      200: z.object({ ok: z.literal(true) }),
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: ErrorSchema,
      503: ErrorSchema,
    },
    summary:
      'A könyv cikkeinek sorrendje (`article.manage`). Az `ids` lehet részhalmaz (a vázlat/archivált/kukás cikkeket a felület nem látja) — azok a lista után kerülnek. Nem készül revízió: a pozíció nem tartalom.',
  },
  editorPublish: {
    method: 'POST',
    path: '/editor/articles/:id/publish',
    pathParams: z.object({ id: z.string().uuid() }),
    body: LifecycleBodySchema,
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Publikálás — a cikk megjelenik az olvasó felületen (`article.publish`).',
  },
  editorUnpublish: {
    method: 'POST',
    path: '/editor/articles/:id/unpublish',
    pathParams: z.object({ id: z.string().uuid() }),
    body: LifecycleBodySchema,
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Publikálás visszavonása — a cikk vázlattá válik (`article.unpublish`).',
  },
  editorArchive: {
    method: 'POST',
    path: '/editor/articles/:id/archive',
    pathParams: z.object({ id: z.string().uuid() }),
    body: LifecycleBodySchema,
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Archiválás — kikerül a navigációból és a keresésből (`article.archive`).',
  },
  editorVisibility: {
    method: 'POST',
    path: '/editor/articles/:id/visibility',
    pathParams: z.object({ id: z.string().uuid() }),
    body: LifecycleBodySchema.extend({ visibility: z.enum(VISIBILITIES) }),
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary:
      'A cikk láthatósága (belső / nyilvános súgó) — `article.publish_public` joggal, OCC-vel.',
  },
  editorTrash: {
    method: 'POST',
    path: '/editor/articles/:id/trash',
    pathParams: z.object({ id: z.string().uuid() }),
    body: LifecycleBodySchema,
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Kukába helyezés (`article.delete`) — visszaállítható, a busz-sor megmarad.',
  },
  editorTrashList: {
    method: 'GET',
    path: '/editor/trash',
    responses: { 200: TrashSchema, 401: ErrorSchema, 403: ErrorSchema, 503: ErrorSchema },
    summary: 'A kukába tett cikkek (`article.delete`) — enélkül a törlés visszafordíthatatlan lenne.',
  },
  editorRestore: {
    method: 'POST',
    path: '/editor/articles/:id/restore',
    pathParams: z.object({ id: z.string().uuid() }),
    body: LifecycleBodySchema,
    responses: {
      200: EditorArticleSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      409: SaveConflictSchema,
      ...UpstreamErrorResponses,
    },
    summary: 'Visszaállítás a kukából VÁZLATKÉNT (`article.delete`) — a publikálás külön döntés.',
  },
  editorAttachments: {
    method: 'GET',
    path: '/editor/articles/:id/attachments',
    pathParams: z.object({ id: z.string().uuid() }),
    responses: {
      200: z.object({ attachments: z.array(AttachmentSchema) }),
      401: ErrorSchema,
      403: ErrorSchema,
    },
    summary: 'A cikk csatolmányai (képek és fájlok).',
  },
  editorUploadUrl: {
    method: 'POST',
    path: '/editor/articles/:id/attachments/upload-url',
    pathParams: z.object({ id: z.string().uuid() }),
    body: UploadUrlBodySchema,
    responses: {
      201: UploadTicketSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      422: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'Presigned feltöltő-jegy — a böngésző ezzel tölt fel közvetlenül az objektum-tárba.',
  },
  editorCommitAttachment: {
    method: 'POST',
    path: '/editor/articles/:id/attachments/:fileId/commit',
    pathParams: z.object({ id: z.string().uuid(), fileId: z.string().uuid() }),
    body: UploadUrlBodySchema.pick({ filename: true, contentType: true }),
    responses: {
      201: CommittedAttachmentSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      422: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'A feltöltés véglegesítése és a csatolmány rögzítése a cikkhez.',
  },
  /**
   * Csatolmány törlése. **Csak akkor, ha a cikk már nem hivatkozik rá** — ellenkező esetben 400,
   * a hibaüzenet megnevezi, hol van még a hivatkozás (törzs vagy vázlat). Így nem lehet egy
   * kattintással törött képet csinálni a cikkben; ugyanaz a szemlélet, mint a „csak üresen
   * törölhető" szekciónál és könyvnél.
   *
   * Nincs OCC: a busz-remove nem támogat `If-Match`-et (lásd `editorDeleteSpace`).
   */
  editorDeleteAttachment: {
    method: 'DELETE',
    path: '/editor/articles/:id/attachments/:fileId',
    pathParams: z.object({ id: z.string().uuid(), fileId: z.string().uuid() }),
    // Üres body: a ts-rest csak így ad mutation-hookot DELETE-re (lásd editorDeleteDraft).
    body: z.object({}).optional(),
    responses: {
      200: z.object({ ok: z.boolean() }),
      400: ErrorSchema,
      401: ErrorSchema,
      403: ErrorSchema,
      404: ErrorSchema,
      503: ErrorSchema,
    },
    summary: 'Csatolmány törlése — csak ha a cikk törzse és a vázlat sem hivatkozik rá.',
  },
  editorDeleteDraft: {
    method: 'DELETE',
    path: '/editor/articles/:id/draft',
    pathParams: z.object({ id: z.string().uuid() }),
    // Üres body: a ts-rest csak így ad mutation-hookot DELETE-re (body nélkül query-ként kezeli).
    body: z.object({}).optional(),
    responses: {
      200: z.object({ ok: z.boolean() }),
      401: ErrorSchema,
      403: ErrorSchema,
    },
    summary: 'A vázlat eldobása (sikeres mentés után, vagy ha a szerkesztő elveti).',
  },
});
